import tw from 'twin.macro';
import { GARM_RISK_LEVEL, IAB_BRAND_SAFETY_RISK_LEVEL } from '@insights_module/constants/garm';
import { GarmRiskLevelType, IabBrandSafetyRiskLevelType } from '@insights_module/types/BrandSafety';
import { CheckMarkIcon } from '@icons/CheckMarkIcon';

const Bar = tw.div`w-[3px] rounded-[1px]`;
const LevelIndicatorWrapper = tw.div`min-w-[15px] min-h-[14px] flex justify-center items-center rounded-[3px]`;
const LevelIndicatorContainer = tw.div`flex flex-row gap-[1px] items-baseline`;

const StyledLabel = tw.div`font-semibold text-[11px] inline-block w-min whitespace-nowrap px-[4px] rounded-[4px] tracking-tight capitalize`;

const INDICATOR_BARS = {
  FIRST: 'first-bar',
  SECOND: 'second-bar',
  THIRD: 'third-bar',
  FOURTH: 'fourth-bar',
};

const RISK_LEVEL_MAPPER = {
  [GARM_RISK_LEVEL.unrelated]: {
    [INDICATOR_BARS.FIRST]: tw`bg-garm-unrelated-muted`,
    [INDICATOR_BARS.SECOND]: tw`bg-garm-unrelated-muted`,
    [INDICATOR_BARS.THIRD]: tw`bg-garm-unrelated-muted`,
    [INDICATOR_BARS.FOURTH]: tw`bg-garm-unrelated-muted`,
    border: tw`border-garm-unrelated-muted`,
    text: tw`text-text-and-icon font-medium`,
    label: tw`bg-garm-unrelated-bg text-text-and-icon-invert`,
  },
  [GARM_RISK_LEVEL.low]: {
    [INDICATOR_BARS.FIRST]: tw`bg-garm-low-dark`,
    [INDICATOR_BARS.SECOND]: tw`bg-garm-low-muted`,
    [INDICATOR_BARS.THIRD]: tw`bg-garm-low-muted`,
    [INDICATOR_BARS.FOURTH]: tw`bg-garm-low-muted`,
    border: tw`border-garm-low-muted`,
    text: tw`text-text-and-icon font-medium`,
    label: tw`bg-garm-low-bg text-text-and-icon-invert`,
  },
  [GARM_RISK_LEVEL.medium]: {
    [INDICATOR_BARS.FIRST]: tw`bg-garm-medium-dark`,
    [INDICATOR_BARS.SECOND]: tw`bg-garm-medium-dark`,
    [INDICATOR_BARS.THIRD]: tw`bg-garm-medium-muted`,
    [INDICATOR_BARS.FOURTH]: tw`bg-garm-medium-muted`,
    border: tw`border-garm-medium-muted`,
    text: tw`text-text-and-icon font-medium`,
    label: tw`bg-garm-medium-bg text-text-and-icon-invert`,
  },
  [GARM_RISK_LEVEL.high]: {
    [INDICATOR_BARS.FIRST]: tw`bg-garm-high-dark`,
    [INDICATOR_BARS.SECOND]: tw`bg-garm-high-dark`,
    [INDICATOR_BARS.THIRD]: tw`bg-garm-high-dark`,
    [INDICATOR_BARS.FOURTH]: tw`bg-garm-high-muted`,
    border: tw`border-garm-high-muted`,
    text: tw`text-text-and-icon font-medium`,
    label: tw`bg-garm-high-bg text-text-and-icon-invert`,
  },
  [GARM_RISK_LEVEL.floor]: {
    [INDICATOR_BARS.FIRST]: tw`bg-garm-floor-dark`,
    [INDICATOR_BARS.SECOND]: tw`bg-garm-floor-dark`,
    [INDICATOR_BARS.THIRD]: tw`bg-garm-floor-dark`,
    [INDICATOR_BARS.FOURTH]: tw`bg-garm-floor-dark`,
    border: tw`border-garm-floor-muted`,
    text: tw`text-text-and-icon font-medium`,
    label: tw`bg-floor-bg text-text-and-icon-invert`,
  },

  [IAB_BRAND_SAFETY_RISK_LEVEL.likely_unrelated]: {
    [INDICATOR_BARS.FIRST]: tw`bg-garm-low-dark`,
    [INDICATOR_BARS.SECOND]: tw`bg-garm-low-muted`,
    [INDICATOR_BARS.THIRD]: tw`bg-garm-low-muted`,
    [INDICATOR_BARS.FOURTH]: tw`bg-garm-low-muted`,
    border: tw`border-garm-low-muted`,
    text: tw`text-text-and-icon font-medium`,
    label: tw`bg-garm-low-bg text-text-and-icon-invert`,
  },
  [IAB_BRAND_SAFETY_RISK_LEVEL.likely_related]: {
    [INDICATOR_BARS.FIRST]: tw`bg-garm-medium-dark`,
    [INDICATOR_BARS.SECOND]: tw`bg-garm-medium-dark`,
    [INDICATOR_BARS.THIRD]: tw`bg-garm-medium-muted`,
    [INDICATOR_BARS.FOURTH]: tw`bg-garm-medium-muted`,
    border: tw`border-garm-medium-muted`,
    text: tw`text-text-and-icon font-medium`,
    label: tw`bg-garm-medium-bg text-text-and-icon-invert`,
  },
  // from BE we are getting likely_unrelated and likely_related for summary endpoints but for details we get likely unrelated and likely related
  // so we need to map it both for now, but we need to check with BE if they are going to change it
  likely_unrelated: {
    [INDICATOR_BARS.FIRST]: tw`bg-garm-low-dark`,
    [INDICATOR_BARS.SECOND]: tw`bg-garm-low-muted`,
    [INDICATOR_BARS.THIRD]: tw`bg-garm-low-muted`,
    [INDICATOR_BARS.FOURTH]: tw`bg-garm-low-muted`,
    border: tw`border-garm-low-muted`,
    text: tw`text-text-and-icon font-medium`,
    label: tw`bg-garm-low-bg text-text-and-icon-invert`,
  },
  likely_related: {
    [INDICATOR_BARS.FIRST]: tw`bg-garm-medium-dark`,
    [INDICATOR_BARS.SECOND]: tw`bg-garm-medium-dark`,
    [INDICATOR_BARS.THIRD]: tw`bg-garm-medium-muted`,
    [INDICATOR_BARS.FOURTH]: tw`bg-garm-medium-muted`,
    border: tw`border-garm-medium-muted`,
    text: tw`text-text-and-icon font-medium`,
    label: tw`bg-garm-medium-bg text-text-and-icon-invert`,
  },
  [IAB_BRAND_SAFETY_RISK_LEVEL.related]: {
    [INDICATOR_BARS.FIRST]: tw`bg-garm-high-dark`,
    [INDICATOR_BARS.SECOND]: tw`bg-garm-high-dark`,
    [INDICATOR_BARS.THIRD]: tw`bg-garm-high-dark`,
    [INDICATOR_BARS.FOURTH]: tw`bg-garm-high-muted`,
    border: tw`border-garm-high-muted`,
    text: tw`text-text-and-icon font-medium`,
    label: tw`bg-garm-high-bg text-text-and-icon-invert`,
  },
};

export const BrandSafetyRiskBadge = ({
  level,
  unrelatedAsCheckmark = false,
  isTextual = false,
  ...rest
}: {
  level: GarmRiskLevelType | IabBrandSafetyRiskLevelType;
  unrelatedAsCheckmark?: boolean;
  isTextual?: boolean;
}) => {
  if (level === IAB_BRAND_SAFETY_RISK_LEVEL.unrelated && unrelatedAsCheckmark) {
    return (
      <LevelIndicatorWrapper {...rest}>
        <CheckMarkIcon tw='stroke-garm-unrelated-dark' />
      </LevelIndicatorWrapper>
    );
  }

  if (isTextual) {
    return (
      <StyledLabel css={RISK_LEVEL_MAPPER[level].label}>{level.replace('_', ' ')}</StyledLabel>
    );
  }

  if (level === GARM_RISK_LEVEL.unrelated || level === IAB_BRAND_SAFETY_RISK_LEVEL.unrelated)
    return null;

  return (
    <LevelIndicatorWrapper {...rest}>
      <LevelIndicatorContainer>
        <Bar tw='h-[6px]' css={RISK_LEVEL_MAPPER[level][INDICATOR_BARS.FIRST]} />
        <Bar tw='h-[9px]' css={RISK_LEVEL_MAPPER[level][INDICATOR_BARS.SECOND]} />
        <Bar tw='h-[12px]' css={RISK_LEVEL_MAPPER[level][INDICATOR_BARS.THIRD]} />
        <Bar tw='h-[14px]' css={RISK_LEVEL_MAPPER[level][INDICATOR_BARS.FOURTH]} />
      </LevelIndicatorContainer>
    </LevelIndicatorWrapper>
  );
};
